import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import {
  Avatar,
  Card,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";

import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closed } from "../../components/drawer/drawerSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import ShadowCard from "../../components/common/ShadowCard";
import {
  phoneWOValueValidator,
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormTextArea from "../../components/formFields/FormTextArea";
import { findClientsByID } from "../NB Inquiry/services/nbinquiry.services";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import {
  AddictionFrequency,
  GETALLACTIVEWITHFILTER_TYPE,
} from "../../_contstants/common";
import { getAllActiveHealthIssue } from "../healthissue/services/healthIssue.services";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { getAllActiveAddiction } from "../addiction/services/addiction.services";
import FormTextField from "../../components/formFields/FormTextField";
import MedicineArray from "./MedicineArray";
import moment from "moment";
import {
  createHealthHistory,
  deleteHealthHistoryReport,
  getHealthHistoryByID,
  updateHealthHistory,
  uploadHealthHistoryReports,
} from "./services/healthHistory.services";
import {
  clearHealthHistoryDetails,
  clearHealthHistoryReportID,
  setHealthHistoryReportID,
} from "./healthHistorySlice";
import ReportsArray from "./ReportsArray";
import IconButton from "../../components/common/IconButton";
import { FaEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";

const CreateHealthHistory: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const ID = location?.state?.ID;
  const LeadID = location?.state?.LeadID;

  const loading = useAppSelector((state) => state.healthHistory.loading);
  const HealthHistoryDetail = useAppSelector(
    (state) => state.healthHistory.HealthHistoryDetail
  );
  const FamilyMemberList = useAppSelector(
    (state) => state.nbinquiry.FamilyMemberList
  );
  const HealthIssueList = useAppSelector(
    (state) => state.healthIssue.HealthIssueList
  );
  const AddictionList = useAppSelector(
    (state) => state.addiction.AddictionList
  );

  const [initialValues, setInitialValues] = useState<any>({});
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    function convertToFormKeys(data: any) {
      const result: any = {
        LeadID: data.LeadID,
        Name: data.Name,
        MobileNo: data.MobileNo,
        Date: data.Date,
        Remark: data.Remark,
        Height: data.Height,
        Weight: data.Weight,
        IsActive: data.IsActive,
        CreatedBy: data.CreatedBy || "",
        HealthIssueDetails: [],
        AddictionDetails: [],
      };

      data?.HealthIssueDetails?.forEach((issue: any) => {
        const id = issue.HealthIssueID;
        result[`selectedIssue_${id}`] = true;
        result[`ProblemSince_${id}`] = issue.ProblemSince;
        result[`ProblemTo_${id}`] = issue.ProblemTo;
        result[`DoctorName_${id}`] = issue.DoctorName;
        result[`DoctorMobileNo_${id}`] = issue.DoctorMobileNo;
        result[`IsSurgery_${id}`] = issue.IsSurgery;
        result[`SurgeryInfo_${id}`] = issue.SurgeryInfo || "";
        result[`Description_${id}`] = issue.Description || "";
        result[`MedicineDetails_${id}`] = issue?.MedicineDetails?.map(
          (med: any) => ({
            Name: med.Name,
            Dose: med?.Dose
              ? med?.Dose?.split(",")?.reduce((arr: any, val: any, i: any) => {
                  if (val?.trim() === "1") arr.push(i + 1);
                  return arr;
                }, [])
              : [],
          })
        );
      });

      data?.AddictionDetails?.forEach((addiction: any) => {
        const id = addiction.AddictionID;
        result[`selectedAddiction_${id}`] = true;
        result[`AddictionFrequency_${id}`] = addiction.Frequency || "";
        result[`AddictionValue_${id}`] = parseInt(addiction.Value || "0");
      });

      return result;
    }
    const formState = convertToFormKeys(HealthHistoryDetail);

    setInitialValues(formState);
  }, [HealthHistoryDetail]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [initialValues]);

  useEffect(() => {
    if (ID) {
      dispatch(getHealthHistoryByID(ID));
    }
    if (LeadID) {
      dispatch(findClientsByID(LeadID));
    }
    dispatch(
      getAllActiveHealthIssue({ OperationType: GETALLACTIVEWITHFILTER_TYPE })
    );
    dispatch(
      getAllActiveAddiction({ OperationType: GETALLACTIVEWITHFILTER_TYPE })
    );

    return () => {
      dispatch(clearHealthHistoryDetails());
      dispatch(closeDialog());
    };
  }, []);

  const handleOpenAttachmentDeleteDialog = (ID: number) => {
    dispatch(openDialog("deleteHealthHistoryReport"));
    dispatch(setHealthHistoryReportID(ID));
  };

  const handleHealthDetailsPartB = (
    e: any,
    ID: number,
    formRenderProps: FormRenderProps
  ) => {
    if (!e?.value) {
      [
        `ProblemSince_${ID}`,
        `ProblemTo_${ID}`,
        `DoctorName_${ID}`,
        `DoctorMobileNo_${ID}`,
        `SurgeryInfo_${ID}`,
        `Description_${ID}`,
      ]?.map((key: string) => {
        formRenderProps.onChange(key, { value: "" });
      });
      [`IsSurgery_${ID}`]?.map((key: string) => {
        formRenderProps.onChange(key, { value: false });
      });
      [`MedicineDetails_${ID}`]?.map((key: string) => {
        formRenderProps.onChange(key, { value: [] });
      });
    }
  };

  const handleHealthDetailsPartC = (
    e: any,
    ID: number,
    formRenderProps: FormRenderProps
  ) => {
    if (!e?.value) {
      [`AddictionFrequency_${ID}`, `AddictionValue_${ID}`]?.map(
        (key: string) => {
          formRenderProps.onChange(key, { value: "" });
        }
      );
    }
  };

  const handleSubmit = async (values: any) => {
    function transformPayload(input: any) {
      const { LeadID, Name, MobileNo, Date, Remark, Height, Weight } = input;

      const HealthIssueDetails = Object.keys(input)
        ?.filter(
          (key) => key.startsWith("selectedIssue_") && input[key] === true
        )
        ?.map((key) => {
          const id = key.split("_")[1];
          const medicine = input[`MedicineDetails_${id}`] || [];
          return {
            HealthIssueID: parseInt(id),
            ProblemSince: input[`ProblemSince_${id}`]
              ? moment(input[`ProblemSince_${id}`]).format("YYYY-MM-DD")
              : null,
            ProblemTo: input[`ProblemTo_${id}`]
              ? moment(input[`ProblemTo_${id}`]).format("YYYY-MM-DD")
              : null,
            DoctorName: input[`DoctorName_${id}`] || "",
            DoctorMobileNo: input[`DoctorMobileNo_${id}`] || "",
            IsSurgery: input[`IsSurgery_${id}`] || false,
            SurgeryInfo: input[`SurgeryInfo_${id}`] || "",
            Description: input[`Description_${id}`] || "",
            MedicineDetails: medicine
              ?.filter((med: any) => med?.Name)
              ?.map((med: any) => ({
                Name: med.Name,
                Dose: Array.isArray(med?.Dose)
                  ? `${med?.Dose?.includes(1) ? "1" : "0"},${
                      med?.Dose?.includes(2) ? "1" : "0"
                    },${med?.Dose?.includes(3) ? "1" : "0"}`
                  : "0,0,0",
              })),
          };
        });

      const AddictionDetails = Object.keys(input)
        ?.filter(
          (key) => key?.startsWith("selectedAddiction_") && input[key] === true
        )
        ?.map((key) => {
          const id = key?.split("_")[1];
          return {
            AddictionID: parseInt(id),
            Frequency: input[`AddictionFrequency_${id}`] || "",
            Value: String(input[`AddictionValue_${id}`] || ""),
          };
        });

      return {
        LeadID,
        Name,
        MobileNo,
        Date: Date ? moment(Date).format("YYYY-MM-DD") : "",
        Remark,
        Height,
        Weight,
        IsActive: true,
        HealthIssueDetails,
        AddictionDetails,
      };
    }

    const payload: any = transformPayload(values);
    if (ID) {
      payload.ID = ID;
    }

    if (ID) {
      try {
        const response = await dispatch(updateHealthHistory(payload));
        if (response.meta?.requestStatus === "fulfilled") {
          if (values.HealthReports?.length > 0) {
            const files = new FormData();
            files.append("HealthID", `${ID}`);
            let AttachmentName: string[] = [];
            values?.HealthReports?.forEach((doc: any) => {
              if (doc?.ReportImage && typeof doc?.ReportImage !== "string") {
                const binaryDoc = doc?.ReportImage[0]?.getRawFile();
                AttachmentName.push(doc?.ReportDesc || " ");
                files.append("FileName", binaryDoc);
              }
            });
            files.append("Description", AttachmentName.join(", "));

            if (files.get("FileName") !== null) {
              await dispatch(uploadHealthHistoryReports(files));
            }
          }
          navigate(-1);
          dispatch(clearHealthHistoryDetails());
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
      }
    } else {
      try {
        const response = await dispatch(createHealthHistory(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values.HealthReports?.length > 0) {
            const files = new FormData();
            files.append("HealthID", `${response?.payload?.ID}`);
            let AttachmentName: string[] = [];
            values?.HealthReports?.forEach((doc: any) => {
              if (doc?.ReportImage && typeof doc?.ReportImage !== "string") {
                const binaryDoc = doc?.ReportImage[0]?.getRawFile();
                AttachmentName.push(doc?.ReportDesc || " ");
                files.append("FileName", binaryDoc);
              }
            });
            files.append("Description", AttachmentName.join(", "));

            if (files.get("FileName") !== null) {
              await dispatch(uploadHealthHistoryReports(files));
            }
          }

          navigate(-1);
          dispatch(clearHealthHistoryDetails());
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
    navigate(-1);
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <DeleteHealthHistoryReportDialog />
      <div className="container-fluid fw-600 pb-2">
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={initialValues}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement className="health-history-form">
              <ShadowCard style={{ padding: 12, marginBottom: 10 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4 className="m-0">
                      {ID ? "Update Health History" : "Create Health History"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Date"
                      label="Date"
                      component={FormDatePicker}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem rowSpan={2}>
                    <Field
                      name="Remark"
                      label="Remarks"
                      placeholder="Remarks"
                      component={FormTextArea}
                      rows={3}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem></GridLayoutItem>
                  <GridLayoutItem style={{ width: "100%" }}>
                    <Field
                      name={"LeadID"}
                      label="Customer"
                      placeholder="Customer"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={FamilyMemberList?.map((familymember: any) => {
                        return {
                          value: familymember.ID,
                          label: `${familymember.FirstName} ${familymember.LastName}`,
                        };
                      })}
                    />
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
              <ShadowCard style={{ padding: 12, marginBottom: 10 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>Health Reports</Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <FieldArray
                      formRenderProps={formRenderProps}
                      component={ReportsArray}
                      name={"HealthReports"}
                    />
                  </GridLayoutItem>
                  {HealthHistoryDetail?.Reports &&
                    HealthHistoryDetail?.Reports?.length > 0 && (
                      <GridLayoutItem
                        colSpan={3}
                        style={{
                          display: "flex",
                          gap: 10,
                          flexWrap: "wrap",
                          marginTop: 10,
                        }}
                      >
                        {HealthHistoryDetail?.Reports?.map((image: any) => (
                          <Card
                            style={{
                              padding: 10,
                              maxWidth: 600,
                              minWidth: 300,
                            }}
                          >
                            <div className="d-flex gap-2 align-items-center">
                              <Avatar
                                style={{
                                  width: 60,
                                  height: 60,
                                  flexBasis: 60,
                                  backgroundColor: "transparent",
                                }}
                                rounded={"full"}
                                type={"image"}
                              >
                                <img
                                  style={{ width: 60, height: 60 }}
                                  src={image?.HealthReport}
                                  alt={"Attachment"}
                                />
                              </Avatar>
                              <div>
                                <p className="mb-1">{image?.Description}</p>
                                <div className="d-flex gap-2">
                                  <IconButton
                                    type="button"
                                    fillMode="solid"
                                    size="small"
                                    title="View"
                                    onClick={() =>
                                      window.open(image?.HealthReport)
                                    }
                                  >
                                    <FaEye
                                      className="absolute-position"
                                      style={{ fontSize: "20px" }}
                                    />
                                  </IconButton>
                                  <IconButton
                                    type="button"
                                    fillMode="solid"
                                    size="small"
                                    title="Delete"
                                    themeColor="error"
                                    onClick={() =>
                                      handleOpenAttachmentDeleteDialog(
                                        image?.ID
                                      )
                                    }
                                  >
                                    <MdDelete
                                      className="absolute-position"
                                      style={{ fontSize: "20px" }}
                                    />
                                  </IconButton>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </GridLayoutItem>
                    )}
                </GridLayout>
              </ShadowCard>
              <ShadowCard style={{ padding: 12, marginBottom: 10 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>Fill Client Health Details</Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <div>
                      <table style={{ width: "100%" }}>
                        <tr>
                          <th colSpan={4}>
                            <h3 className="text-center mt-3">
                              આરોગ્ય ઘોષણા પત્રક
                            </h3>
                            <h5 className="text-center">
                              (જીવન વીમા માટે મેડિક્લેમ, પ્રવાસ, વ્યક્તિગત
                              અકસ્માત અને આરોગ્ય સાથે સંબંધિત કોઈપણ અન્ય
                              ઉત્પાદન)
                            </h5>
                            <p className="text-center m-0">
                              (માત્ર ગ્રાહક દ્વારા ભરવામાં આવશે)
                            </p>
                          </th>
                        </tr>
                        <tr style={{ background: "black", color: "#fff" }}>
                          <th style={{ width: 50 }}>અ.નં.</th>
                          <th colSpan={3} className="text-center">
                            વિભાગ એ - વ્યક્તિગત ઇતિહાસજણાવો
                          </th>
                        </tr>
                        <tr>
                          <td rowSpan={2}>1</td>
                          <td rowSpan={2}>
                            કૃપા કરી ચોક્કસ ઉંચાઈ અને વજન જણાવો.
                          </td>
                          <td style={{ width: "25%" }}>ઉંચાઈ (સે.મી.)</td>
                          <td style={{ width: "20%" }}>
                            <Field
                              wrapperClassName="m-0 right-aligned-field"
                              name="Height"
                              placeholder="0"
                              component={FormNumericTextField}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>વજન (કી.ગ્રા.)</td>
                          <td>
                            <Field
                              wrapperClassName="m-0 right-aligned-field"
                              name="Weight"
                              placeholder="0"
                              component={FormNumericTextField}
                            />
                          </td>
                        </tr>
                        <tr style={{ background: "black", color: "#fff" }}>
                          <th style={{ width: 50 }}>અ.નં.</th>
                          <th colSpan={3} className="text-center">
                            વિભાગ બી : કોઈ પણ વ્યક્તિ પીડિત હતા / હાલમાં પીડિત
                            છે
                          </th>
                        </tr>
                        {HealthIssueList &&
                          HealthIssueList?.length > 0 &&
                          HealthIssueList?.map((issue: any, index: number) => {
                            return (
                              <>
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td colSpan={2}>{issue?.Name}</td>
                                  <td className="text-center">
                                    <Field
                                      wrapperClassName="m-0"
                                      name={`selectedIssue_${issue?.ID}`}
                                      label=""
                                      component={FormCheckbox}
                                      onChange={(e: any) =>
                                        handleHealthDetailsPartB(
                                          e,
                                          issue?.ID,
                                          formRenderProps
                                        )
                                      }
                                      style={{ border: "1px solid gray" }}
                                    />
                                  </td>
                                </tr>
                                {formRenderProps.valueGetter(
                                  `selectedIssue_${issue?.ID}`
                                ) && (
                                  <tr key={index}>
                                    <td></td>
                                    <td colSpan={3}>
                                      <p className="text-success fw-bold">
                                        + Health Problem Details:
                                      </p>
                                      <div className="row ms-1 me-1">
                                        <div className="col-6">
                                          <div className="row p-0">
                                            <div className="col-3 p-0 d-flex align-items-center mb-2">
                                              Problem Since
                                            </div>
                                            <div className="col-3 p-0 mb-2">
                                              <Field
                                                wrapperClassName="m-0"
                                                name={`ProblemSince_${issue?.ID}`}
                                                format="MMMM yyyy"
                                                component={FormDatePicker}
                                              />
                                            </div>
                                            <div className="col-3 p-0 ps-2 d-flex align-items-center mb-2">
                                              To
                                            </div>
                                            <div className="col-3 p-0 mb-2">
                                              <Field
                                                wrapperClassName="m-0"
                                                name={`ProblemTo_${issue?.ID}`}
                                                component={FormDatePicker}
                                                format="MMMM yyyy"
                                                minDate={formRenderProps.valueGetter(
                                                  `ProblemSince_${issue?.ID}`
                                                )}
                                              />
                                            </div>
                                            <div className="col-3 p-0 d-flex align-items-center mb-2">
                                              Doctor Name
                                            </div>
                                            <div className="col-3 p-0 mb-2">
                                              <Field
                                                wrapperClassName="m-0"
                                                name={`DoctorName_${issue?.ID}`}
                                                component={FormTextField}
                                                placeholder="Doctor Name"
                                              />
                                            </div>
                                            <div className="col-3 p-0 ps-2 d-flex align-items-center mb-2">
                                              Mobile No.
                                            </div>
                                            <div className="col-3 p-0 mb-2">
                                              <Field
                                                wrapperClassName="m-0"
                                                name={`DoctorMobileNo_${issue?.ID}`}
                                                component={FormTextField}
                                                type="number"
                                                max="10"
                                                validator={
                                                  phoneWOValueValidator
                                                }
                                                placeholder="i.e. 9999999999"
                                              />
                                            </div>
                                            <div className="col-3 p-0 d-flex align-items-center mb-3">
                                              Any Type Surgery?
                                            </div>
                                            <div className="col-9 p-0 mb-3">
                                              <Field
                                                wrapperClassName="m-0"
                                                name={`IsSurgery_${issue?.ID}`}
                                                label=""
                                                component={FormCheckbox}
                                                onChange={(e: any) => {
                                                  if (!e?.value) {
                                                    formRenderProps.onChange(
                                                      `SurgeryInfo_${issue?.ID}`,
                                                      {
                                                        value: "",
                                                      }
                                                    );
                                                  }
                                                }}
                                                style={{
                                                  border: "1px solid gray",
                                                }}
                                              />
                                            </div>
                                            {formRenderProps.valueGetter(
                                              `IsSurgery_${issue?.ID}`
                                            ) && (
                                              <>
                                                <div className="col-12 p-0 ">
                                                  Surgery Information
                                                </div>
                                                <div className="col-12 p-0 mb-2">
                                                  <Field
                                                    wrapperClassName="m-0"
                                                    placeholder="Description / Other Information"
                                                    name={`SurgeryInfo_${issue?.ID}`}
                                                    component={FormTextArea}
                                                    rows={2}
                                                  />
                                                </div>
                                              </>
                                            )}
                                            <div className="col-12 p-0 ">
                                              Description / Other Information
                                            </div>
                                            <div className="col-12 p-0 mb-2">
                                              <Field
                                                wrapperClassName="m-0"
                                                placeholder="Description / Other Information"
                                                name={`Description_${issue?.ID}`}
                                                component={FormTextArea}
                                                rows={2}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-6 pe-0">
                                          <table>
                                            <tr>
                                              <th>Medicine Name</th>
                                              <th style={{ width: 135 }}>
                                                Dose
                                              </th>
                                              <th style={{ width: 40 }}></th>
                                            </tr>
                                            <FieldArray
                                              formRenderProps={formRenderProps}
                                              component={MedicineArray}
                                              name={`MedicineDetails_${issue?.ID}`}
                                            />
                                          </table>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </>
                            );
                          })}
                        <tr style={{ background: "black", color: "#fff" }}>
                          <th style={{ width: 50 }}></th>
                          <th colSpan={3} className="text-center">
                            વિભાગ સી : કોઈપણ વ્યક્તિ ઉપાર્જન કરે છે અથવા ક્યારેય
                            ઉપયોગ થાય છે
                          </th>
                        </tr>
                        {AddictionList &&
                          AddictionList?.length > 0 &&
                          AddictionList?.map(
                            (addiction: any, index: number) => {
                              return (
                                <>
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td colSpan={2}>{addiction?.Name}</td>
                                    <td className="text-center">
                                      <Field
                                        wrapperClassName="m-0"
                                        name={`selectedAddiction_${addiction?.ID}`}
                                        label=""
                                        component={FormCheckbox}
                                        onChange={(e: any) =>
                                          handleHealthDetailsPartC(
                                            e,
                                            addiction?.ID,
                                            formRenderProps
                                          )
                                        }
                                        style={{ border: "1px solid gray" }}
                                      />
                                    </td>
                                  </tr>
                                  {formRenderProps.valueGetter(
                                    `selectedAddiction_${addiction?.ID}`
                                  ) && (
                                    <tr key={index}>
                                      <td></td>
                                      <td colSpan={3}>
                                        <table>
                                          <tr>
                                            <td
                                              className="pt-0 pb-0"
                                              style={{
                                                width: "25%",
                                                border: "none",
                                              }}
                                            >
                                              How Many Times?
                                            </td>
                                            <td
                                              className="pt-0 pb-0"
                                              style={{
                                                width: "25%",
                                                border: "none",
                                              }}
                                            >
                                              <Field
                                                wrapperClassName="m-0"
                                                name={`AddictionFrequency_${addiction?.ID}`}
                                                component={FormSelectionField}
                                                placeholder="Frequency"
                                                validator={
                                                  formRenderProps.valueGetter(
                                                    `AddictionValue_${addiction?.ID}`
                                                  )
                                                    ? requiredValidator
                                                    : skipValidator
                                                }
                                                options={AddictionFrequency?.map(
                                                  (status: any) => {
                                                    return {
                                                      value: status?.value,
                                                      label: status?.label,
                                                    };
                                                  }
                                                )}
                                              />
                                            </td>
                                            <td
                                              className="pt-0 pb-0"
                                              style={{
                                                width: "25%",
                                                border: "none",
                                              }}
                                            >
                                              How Much?
                                            </td>
                                            <td
                                              className="pt-0 pb-0"
                                              style={{
                                                width: "25%",
                                                border: "none",
                                              }}
                                            >
                                              <Field
                                                wrapperClassName="m-0 right-aligned-field"
                                                name={`AddictionValue_${addiction?.ID}`}
                                                placeholder="0"
                                                component={FormNumericTextField}
                                                validator={
                                                  formRenderProps.valueGetter(
                                                    `AddictionFrequency_${addiction?.ID}`
                                                  )
                                                    ? requiredValidator
                                                    : skipValidator
                                                }
                                              />
                                            </td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            }
                          )}
                        <tr style={{ background: "black", color: "#fff" }}>
                          <th style={{ width: 50 }}></th>
                          <th colSpan={3} className="text-center">
                            વિભાગ ડી : નામ અને ફેમિલી ડોક્ટરની સંપર્ક વિગતો
                          </th>
                        </tr>
                        <tr>
                          <td>1</td>
                          <td>નામ :</td>
                          <td style={{ width: "20%" }} colSpan={2}>
                            <Field
                              wrapperClassName="m-0"
                              name="Name"
                              placeholder="i.e. John Smith"
                              component={FormTextField}
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>2</td>
                          <td>મોબાઈલ નંબર :</td>
                          <td style={{ width: "20%" }} colSpan={2}>
                            <Field
                              wrapperClassName="m-0"
                              name="MobileNo"
                              placeholder="i.e. 9999999999"
                              component={FormTextField}
                              validator={phoneWOValueValidator}
                              type="number"
                              max="10"
                            />
                          </td>
                        </tr>
                      </table>
                    </div>
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      textAlign: "end",
                      marginTop: "20px",
                      width: "100%",
                    }}
                  >
                    <ButtonWithLoading
                      label={ID ? "Update" : "Create"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 5 }}
                      onClick={handleFormClose}
                    >
                      Cancel
                    </Button>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

const DeleteHealthHistoryReportDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const ID = location?.state?.ID;
  const HealthHistoryReportID = useAppSelector(
    (state) => state.healthHistory.HealthHistoryReportID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ReportID: number | null) => {
    if (ReportID) {
      const response = await dispatch(deleteHealthHistoryReport(ReportID));
      if (response.meta.requestStatus === "fulfilled") {
        if (ID) {
          dispatch(getHealthHistoryByID(ID));
        }
        dispatch(closeDialog());
        dispatch(clearHealthHistoryReportID());
      } else {
        if (ID) {
          dispatch(getHealthHistoryByID(ID));
        }
        dispatch(closeDialog());
        dispatch(clearHealthHistoryReportID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteHealthHistoryReport" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Image?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Image?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => {
                    handleDeleteAction(HealthHistoryReportID);
                  }}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CreateHealthHistory;
